"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import { useAuth } from "../../../../context/AuthContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import FollowUpDateTimePicker from "../components/FollowUpDateTimePicker";
import styles from "../css/review-follow-up.module.css";
import {
  getConsultationFull,
} from "../../../../api/clinic/consultationApi.js";
import { getAppointments as fetchAppointmentsApi } from "../../../../api/clinic/bookingApi.js";

export default function ReviewFollowUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultationData, previousStep, getStepDraft, setStepDraft } = useConsultation();
  const { user } = useAuth();
  const isPCDT = Number(user?.roleid) === 8;
  const [saving, setSaving] = useState(false);
  const [fullData, setFullData] = useState(null);

  // Check if user is in PCDT flow (came from treatment service)
  const serviceWorkflowData = getStepDraft('serviceWorkflowData', {});
  const hasServiceWorkflowData = Object.keys(serviceWorkflowData).length > 0;
  const returnedFromWorkflow = consultationData?.returnedFromWorkflow || false;
  const isInPCDTFlow = isPCDT && hasServiceWorkflowData && returnedFromWorkflow;

  const draftKey = "reviewFollowUp";
  
  const defaultFormData = {
    followUpDate: null,
    followUpDateText: "",
    followUpTime: "",
    followUpNotes: "",
  };

  const [formData, setFormData] = useState(() => getStepDraft(draftKey, defaultFormData));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handleFollowUpChange = (next) => {
    setFormData(next);
    setStepDraft(draftKey, next);
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/prescription");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = consultationData?.consultationId;
        if (!id) return;
        const full = await getConsultationFull(id);
        if (cancelled) return;
        setFullData(full);

        const f = full?.consultation_followup;
        if (f) {
          const existingDraft = getStepDraft(draftKey, null);
          if (existingDraft) return;

          const next = {
            ...defaultFormData,
            followUpDate: f.followupdate ? new Date(f.followupdate) : null,
            followUpNotes: f.followupnotes ?? "",
          };
          setFormData(next);
          setStepDraft(draftKey, next);
        }
      } catch (e) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.consultationId]);

  const handleInvoice = async () => {
    try {
      setSaving(true);
      navigate("/customer/clinic/invoice", {
        state: {
          ...(location?.state || {}),
          returnedFromWorkflow: true,
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const patientInfo = {
    name: `${consultationData?.patient?.firstname || ""} ${consultationData?.patient?.surname || ""}`.trim() || "—",
    id: consultationData?.patient?.idnumber || "—",
    age: consultationData?.patient?.age ? String(consultationData.patient.age) : "—",
    gender: consultationData?.patient?.sex || "—",
  };

  const vitalsDraft = getStepDraft("vitalsObservations", null);
  const symptomsDraft = getStepDraft("symptomsExam", null);
  const diagnosisDraft = getStepDraft("diagnosisICD10", null);
  const prescriptionDraft = getStepDraft("prescription", null);

  const v = fullData?.consultation_vitals;
  const vitalsSummary = {
    bp: vitalsDraft
      ? `${vitalsDraft.bpSystolic ?? "—"}/${vitalsDraft.bpDiastolic ?? "—"}`
      : (v ? `${v.bpsystolic ?? "—"}/${v.bpdiastolic ?? "—"}` : "—"),
    temp: vitalsDraft?.temperature ?? v?.temperature ?? "—",
    bmi: vitalsDraft?.bmi ?? v?.bmi ?? "—",
    glucose: vitalsDraft?.glucose ?? v?.glucose ?? "—",
    weight: vitalsDraft?.weight ?? v?.weight ?? "—",
    pulse: vitalsDraft?.pulse ?? v?.pulse ?? "—",
  };

  const diagnosisFromDraft = Array.isArray(diagnosisDraft?.selectedDiagnoses)
    ? diagnosisDraft.selectedDiagnoses
    : [];

  const primaryDiagnosisFromDraft = diagnosisFromDraft.length
    ? `${diagnosisFromDraft[0]?.code || ""} ${diagnosisFromDraft[0]?.description || ""}`.trim()
    : "";

  const complaintDiagnosis = {
    complaint:
      symptomsDraft?.mainComplaint ||
      fullData?.consultation_complaint?.maincomplaint ||
      "Not recorded",
    diagnosis:
      primaryDiagnosisFromDraft ||
      ((Array.isArray(fullData?.consultation_diagnosis) && fullData.consultation_diagnosis.length)
        ? `${fullData.consultation_diagnosis[0]?.icd10code || ""} ${fullData.consultation_diagnosis[0]?.icd10description || ""}`.trim()
        : "Not recorded"),
  };

  const prescriptions = Array.isArray(prescriptionDraft?.medications)
    ? prescriptionDraft.medications
        .map((m) => ({
          name: m?.drugName || "",
        }))
        .filter((x) => !!String(x.name || "").trim())
    : [];

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={10}
            totalSteps={10}
            stepTitle="Review & Follow-Up"
            stepDetails="Step 10 of 10 · Maria van der Merwe · General Consultation"
            onPrevious={handlePrevious}
            onNext={handleInvoice}
            nextLabel="Invoice"
            showNextDropdown={true}
            nextDisabled={saving}
          />

          <div className={styles.formContainer}>
            {/* Top Row - Patient and Vitals */}
            <div className={styles.summaryRow}>
              {/* PATIENT */}
              <div className={styles.summaryCard}>
                <h3 className={styles.blueSubHeader}>PATIENT</h3>
                <div className={styles.patientInfo}>
                  <p className={styles.patientName}>{patientInfo.name}</p>
                  <p className={styles.patientDetails}>{patientInfo.id} · {patientInfo.age} · {patientInfo.gender}</p>
                </div>
              </div>

              {/* VITALS SUMMARY */}
              <div className={styles.summaryCard}>
                <h3 className={styles.blueSubHeader}>VITALS SUMMARY</h3>
                <div className={styles.vitalsGrid}>
                  <div className={styles.vitalItem}>
                    <span className={styles.vitalLabel}>BP:</span>
                    <span className={styles.vitalValue}>{vitalsSummary.bp}</span>
                  </div>
                  <div className={styles.vitalItem}>
                    <span className={styles.vitalLabel}>Temp:</span>
                    <span className={styles.vitalValue}>{vitalsSummary.temp}</span>
                  </div>
                  <div className={styles.vitalItem}>
                    <span className={styles.vitalLabel}>Pulse:</span>
                    <span className={styles.vitalValue}>{vitalsSummary.pulse}</span>
                  </div>
                  <div className={styles.vitalItem}>
                    <span className={styles.vitalLabel}>BMI:</span>
                    <span className={styles.vitalValue}>{vitalsSummary.bmi}</span>
                  </div>
                  <div className={styles.vitalItem}>
                    <span className={styles.vitalLabel}>Glucose:</span>
                    <span className={styles.vitalValue}>{vitalsSummary.glucose}</span>
                  </div>
                  <div className={styles.vitalItem}>
                    <span className={styles.vitalLabel}>Weight:</span>
                    <span className={styles.vitalValue}>{vitalsSummary.weight}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Middle Row - Complaint and Prescriptions */}
            <div className={styles.summaryRow}>
              {/* COMPLAINT & DIAGNOSIS */}
              <div className={styles.summaryCard}>
                <h3 className={styles.blueSubHeader}>COMPLAINT & DIAGNOSIS</h3>
                <div className={styles.complaintContent}>
                  <p className={styles.complaintText}>{complaintDiagnosis.complaint}</p>
                  <p className={styles.diagnosisText}>{complaintDiagnosis.diagnosis}</p>
                </div>
              </div>

              {/* PRESCRIPTIONS */}
              <div className={styles.summaryCard}>
                <h3 className={styles.blueSubHeader}>PRESCRIPTIONS</h3>
                <div className={styles.prescriptionsContent}>
                  {prescriptions.length === 0 ? (
                    <p className={styles.noPrescriptions}>No prescriptions</p>
                  ) : (
                    prescriptions.map((prescription, index) => (
                      <div key={index} className={styles.prescriptionItem}>
                        {prescription.name}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* FOLLOW-UP Section */}
            <div className={styles.followUpCard}>
              <h3 className={styles.blueSubHeader}>
                <span className={styles.calendarIcon}>📅</span> FOLLOW-UP
              </h3>
              <div className={styles.followUpRow}>
                {/* Follow-up Date */}
                <div className={styles.followUpItem}>
                  <label>Follow-up Date</label>
                  <FollowUpDateTimePicker
                    value={formData}
                    onChange={handleFollowUpChange}
                    getAppointments={() => fetchAppointmentsApi()}
                  />
                </div>

                {/* Follow-up Notes */}
                <div className={styles.followUpItem}>
                  <label>Follow-up Notes</label>
                  <textarea
                    name="followUpNotes"
                    value={formData.followUpNotes}
                    onChange={handleInputChange}
                    placeholder="Follow-up instructions..."
                    className={styles.followUpTextarea}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
